/**
 * The mailbox connect guide of Outreach (design `outreach-v2/o Mailbox Connect Guide`, table o3).
 * One entry for each mailbox kind: the domains and the MX names that find it, the IMAP and SMTP
 * servers, and the 3-step guide. The web and both apps render the guide from this file.
 *
 * The step texts are English product copy. A client translates them by id:
 * web `app.sml.outreach.mailboxGuide.<providerId>.<stepKey>.title|body|link` and
 * `app.sml.outreach.mailboxGuide.<providerId>.help<n>` (n starts at 1).
 */

/** Every provider id, in the order of the tiles. A tuple, so `z.enum` can take it. */
export const MAILBOX_PROVIDER_IDS = [
  'gmail',
  'outlook',
  'microsoft365',
  'icloud',
  'yahoo',
  'aol',
  'zoho',
  'fastmail',
  'godaddy',
  'ionos',
  'hostinger',
  'proton',
  'other',
] as const;

export type MailboxProviderId = (typeof MAILBOX_PROVIDER_IDS)[number];

export interface HostSpec {
  host: string;
  port: number;
  /** `ssl`: TLS from the start (993, 465). `starttls`: plain connect, then STARTTLS (143, 587). */
  security: 'ssl' | 'starttls';
}

export interface MailboxGuideStep {
  /** The stable id of the step inside its provider (translation ids). */
  key: string;
  title: string;
  body: string;
  linkLabel?: string;
  /** No URL with a label: the client opens its own screen (the admin message, the server help). */
  linkUrl?: string;
}

export interface MailboxProvider {
  id: MailboxProviderId;
  label: string;
  /** The small line under the label on the provider tile (frame o1). */
  tile: string;
  /** The heading and the intro of the guide card (frame o2). */
  guideTitle: string;
  guideIntro: string;
  /** Mail domains. `name.*` matches every top-level domain (`hotmail.*` = hotmail.com, hotmail.co.uk). */
  domains: string[];
  /** MX host suffixes: an MX host that is one of them, or ends with `.<suffix>`, belongs to this provider. */
  mxPatterns: string[];
  imap: HostSpec | null;
  smtp: HostSpec | null;
  /** What the person types: an app password, the normal email password, or nothing (not supported). */
  passwordKind: 'appPassword' | 'emailPassword' | 'unsupported';
  /** True when a work admin must allow the mailbox first (frame o5). */
  adminNeeded: boolean;
  steps: MailboxGuideStep[];
  help: string[];
}

const ssl = (host: string, port: number): HostSpec => ({ host, port, security: 'ssl' });
const starttls = (host: string, port: number): HostSpec => ({ host, port, security: 'starttls' });

const GOOGLE_TWO_STEP_URL = 'https://myaccount.google.com/signinoptions/two-step-verification';
const GOOGLE_APP_PASSWORDS_URL = 'https://myaccount.google.com/apppasswords';

const webHostPasswordStep = (host: string): MailboxGuideStep => ({
  key: 'password',
  title: 'Find your email password',
  body: `It is the normal password of this email: the one you use to read it in the webmail of ${host}. No app password is needed.`,
});

const webHostServersStep = (host: string): MailboxGuideStep => ({
  key: 'servers',
  title: 'We fill in the servers',
  body: `We know the mail servers of ${host}, so there is nothing to type here. If your email at ${host} runs on Microsoft 365, it does not work with Music24.`,
});

const typePasswordStep: MailboxGuideStep = {
  key: 'paste',
  title: 'Type the password here',
  body: 'We check it right away. We send nothing and read nothing during the check.',
};

/** The one line of the Outlook and Microsoft 365 guides (controller ruling 2026-10-23). */
export const MICROSOFT_UNSUPPORTED =
  "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.";

/** The 3 cards of an unsupported Microsoft mailbox: the reason, then the way to another mailbox. */
const microsoftUnsupportedSteps: MailboxGuideStep[] = [
  { key: 'why', title: 'Microsoft keeps apps out', body: MICROSOFT_UNSUPPORTED },
  {
    key: 'other',
    title: 'Use another mailbox',
    body: 'A Gmail address or the email of your web host works in a few minutes.',
    linkLabel: 'Use another mailbox',
  },
  {
    key: 'type',
    title: 'Type the other address',
    body: 'Go back to the first step and type the address curators should answer.',
  },
];

export const MAILBOX_PROVIDERS: MailboxProvider[] = [
  {
    id: 'gmail',
    label: 'Gmail',
    tile: 'Also Google Workspace',
    guideTitle: 'Make a Gmail app password',
    guideIntro:
      'Google does not let other apps use your normal password. An app password is a separate 16-letter code just for Music24. You can delete it any time in your Google account.',
    domains: ['gmail.com', 'googlemail.com'],
    mxPatterns: ['google.com', 'googlemail.com'],
    imap: ssl('imap.gmail.com', 993),
    smtp: ssl('smtp.gmail.com', 465),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'twoStep',
        title: 'Turn on 2-Step Verification',
        body: 'Google asks for this before it shows app passwords. If it is on already, skip this step.',
        linkLabel: 'Open Google security',
        linkUrl: GOOGLE_TWO_STEP_URL,
      },
      {
        key: 'create',
        title: 'Make the app password',
        body: 'Sign in with the address you pitch from. Type "Music24" as the name and press Create. Google shows a yellow box with 16 letters.',
        linkLabel: 'Open App passwords',
        linkUrl: GOOGLE_APP_PASSWORDS_URL,
      },
      {
        key: 'paste',
        title: 'Copy the 16 letters and paste them here',
        body: 'Spaces are fine.',
      },
    ],
    help: [
      '2-Step Verification is still off: do step 1 first.',
      'A work or school account: your admin may have turned app passwords off. Send your admin a message (we write it for you).',
      'Advanced Protection is on: Google blocks app passwords. Use another mailbox.',
    ],
  },
  {
    id: 'outlook',
    label: 'Outlook',
    tile: 'Not supported',
    guideTitle: 'Outlook does not work with Music24',
    guideIntro: MICROSOFT_UNSUPPORTED,
    domains: ['outlook.*', 'hotmail.*', 'live.*', 'msn.com', 'passport.com'],
    mxPatterns: ['olc.protection.outlook.com', 'hotmail.com'],
    imap: null,
    smtp: null,
    passwordKind: 'unsupported',
    adminNeeded: false,
    steps: microsoftUnsupportedSteps,
    help: [MICROSOFT_UNSUPPORTED],
  },
  {
    id: 'microsoft365',
    label: 'Microsoft 365',
    tile: 'Not supported',
    guideTitle: 'Microsoft 365 does not work with Music24',
    guideIntro: MICROSOFT_UNSUPPORTED,
    domains: [],
    mxPatterns: ['protection.outlook.com', 'outlook.com'],
    imap: null,
    smtp: null,
    passwordKind: 'unsupported',
    adminNeeded: false,
    steps: microsoftUnsupportedSteps,
    help: [MICROSOFT_UNSUPPORTED],
  },
  {
    id: 'icloud',
    label: 'iCloud Mail',
    tile: 'icloud.com, me.com',
    guideTitle: 'Make an iCloud app-specific password',
    guideIntro:
      'Apple does not let other apps use your Apple Account password. An app-specific password is a separate code just for Music24. You can delete it any time.',
    domains: ['icloud.com', 'me.com', 'mac.com'],
    mxPatterns: ['mail.icloud.com'],
    imap: ssl('imap.mail.me.com', 993),
    smtp: starttls('smtp.mail.me.com', 587),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'twoStep',
        title: 'Check two-factor authentication',
        body: 'Apple shows app-specific passwords only with two-factor authentication. Most Apple Accounts have it on already.',
        linkLabel: 'Open Apple Account',
        linkUrl: 'https://account.apple.com',
      },
      {
        key: 'create',
        title: 'Make an app-specific password',
        body: 'Go to Sign-In and Security, then App-Specific Passwords. Type "Music24" as the name. Apple shows a password like abcd-efgh-ijkl-mnop.',
        linkLabel: 'Open Apple Account',
        linkUrl: 'https://account.apple.com',
      },
      {
        key: 'paste',
        title: 'Copy the password and paste it here',
        body: 'The login is your iCloud address, also when you send from your own domain.',
      },
    ],
    help: [
      'The login is the iCloud address (name@icloud.com), even with a custom domain.',
      'No App-Specific Passwords section: two-factor authentication is off. Do step 1 first.',
    ],
  },
  {
    id: 'yahoo',
    label: 'Yahoo Mail',
    tile: 'Also AOL',
    guideTitle: 'Make a Yahoo app password',
    guideIntro:
      'Yahoo does not let other apps use your normal password. An app password is a separate code just for Music24. You can delete it any time.',
    domains: ['yahoo.*', 'ymail.com', 'rocketmail.com'],
    mxPatterns: ['yahoodns.net'],
    imap: ssl('imap.mail.yahoo.com', 993),
    smtp: ssl('smtp.mail.yahoo.com', 465),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'security',
        title: 'Open your account security',
        body: 'Sign in with the address you pitch from.',
        linkLabel: 'Open Yahoo security',
        linkUrl: 'https://login.yahoo.com/account/security',
      },
      {
        key: 'create',
        title: 'Generate an app password',
        body: 'Press "Generate app password", type "Music24" as the name and press Generate password.',
      },
      {
        key: 'paste',
        title: 'Copy the password and paste it here',
        body: 'Yahoo shows it only once. Spaces are fine.',
      },
    ],
    help: ['No "Generate app password" button: Yahoo asks you to turn on two-step verification first.'],
  },
  {
    id: 'aol',
    label: 'AOL Mail',
    tile: 'aol.com',
    guideTitle: 'Make an AOL app password',
    guideIntro:
      'AOL does not let other apps use your normal password. An app password is a separate code just for Music24. You can delete it any time.',
    domains: ['aol.*', 'aim.com'],
    mxPatterns: [],
    imap: ssl('imap.aol.com', 993),
    smtp: ssl('smtp.aol.com', 465),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'security',
        title: 'Open your account security',
        body: 'Sign in with the address you pitch from.',
        linkLabel: 'Open AOL security',
        linkUrl: 'https://login.aol.com/account/security',
      },
      {
        key: 'create',
        title: 'Generate an app password',
        body: 'Press "Generate app password", type "Music24" as the name and press Generate password.',
      },
      {
        key: 'paste',
        title: 'Copy the password and paste it here',
        body: 'AOL shows it only once. Spaces are fine.',
      },
    ],
    help: ['No "Generate app password" button: AOL asks you to turn on two-step verification first.'],
  },
  {
    id: 'zoho',
    label: 'Zoho Mail',
    tile: 'Free and paid',
    guideTitle: 'Make a Zoho app password',
    guideIntro: 'Zoho needs IMAP access on and an app password just for Music24. You can delete it any time.',
    domains: ['zoho.*', 'zohomail.*'],
    mxPatterns: ['zoho.com', 'zoho.eu', 'zoho.in', 'zoho.com.au', 'zohomail.com'],
    imap: ssl('imap.zoho.com', 993),
    smtp: ssl('smtp.zoho.com', 465),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'imap',
        title: 'Turn on IMAP access',
        body: 'In Zoho Mail, open Settings, then Mail accounts, then IMAP, and turn IMAP access on.',
        linkLabel: 'Open Zoho settings',
        linkUrl: 'https://mail.zoho.com/zm/#settings/all/mailaccounts',
      },
      {
        key: 'create',
        title: 'Make an app password',
        body: 'Open Security, then App passwords. Press Generate new password and type "Music24" as the name.',
        linkLabel: 'Open Zoho security',
        linkUrl: 'https://accounts.zoho.com/home#security/app_password',
      },
      {
        key: 'paste',
        title: 'Copy the password and paste it here',
        body: 'Zoho shows it only once.',
      },
    ],
    help: [
      'Zoho answered "You are yet to enable IMAP": do step 1 first.',
      'An EU account (zoho.eu): we use the zoho.eu servers for you.',
    ],
  },
  {
    id: 'fastmail',
    label: 'Fastmail',
    tile: '',
    guideTitle: 'Make a Fastmail app password',
    guideIntro:
      'Fastmail does not let other apps use your normal password. An app password is a separate code just for Music24.',
    domains: ['fastmail.*', 'fastmail.fm'],
    mxPatterns: ['messagingengine.com'],
    imap: ssl('imap.fastmail.com', 993),
    smtp: ssl('smtp.fastmail.com', 465),
    passwordKind: 'appPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'settings',
        title: 'Open your app passwords',
        body: 'Go to Settings, then Privacy & Security, then Manage app passwords and access.',
        linkLabel: 'Open Fastmail settings',
        linkUrl: 'https://app.fastmail.com/settings/security/devicekeys',
      },
      {
        key: 'create',
        title: 'Make a new app password',
        body: 'Press New app password. Choose the access "Mail (IMAP/POP/SMTP)" and type "Music24" as the name.',
      },
      {
        key: 'paste',
        title: 'Copy the password and paste it here',
        body: 'Fastmail shows it only once. Spaces are fine.',
      },
    ],
    help: ['Choose "Mail (IMAP/POP/SMTP)" as the access. A password for other access does not work.'],
  },
  {
    id: 'godaddy',
    label: 'GoDaddy',
    tile: 'Workspace Email',
    guideTitle: 'Your email password',
    guideIntro: 'For a mailbox at GoDaddy, the normal email password works.',
    domains: [],
    mxPatterns: ['secureserver.net'],
    imap: ssl('imap.secureserver.net', 993),
    smtp: ssl('smtpout.secureserver.net', 465),
    passwordKind: 'emailPassword',
    adminNeeded: false,
    steps: [
      { ...webHostPasswordStep('GoDaddy'), linkLabel: 'Open GoDaddy webmail', linkUrl: 'https://email.godaddy.com' },
      webHostServersStep('GoDaddy'),
      typePasswordStep,
    ],
    help: ['GoDaddy email that runs on Microsoft 365 does not work with Music24. Use another mailbox.'],
  },
  {
    id: 'ionos',
    label: 'IONOS',
    tile: 'Mail Basic, Mail Business',
    guideTitle: 'Your email password',
    guideIntro: 'For a mailbox at IONOS, the normal email password works.',
    domains: [],
    mxPatterns: ['ionos.com', 'ionos.de', 'ionos.co.uk', 'ionos.es', 'ionos.fr', 'ionos.it', 'kundenserver.de'],
    imap: ssl('imap.ionos.com', 993),
    smtp: ssl('smtp.ionos.com', 465),
    passwordKind: 'emailPassword',
    adminNeeded: false,
    steps: [
      { ...webHostPasswordStep('IONOS'), linkLabel: 'Open IONOS webmail', linkUrl: 'https://mail.ionos.com' },
      webHostServersStep('IONOS'),
      typePasswordStep,
    ],
    help: ['You forgot the password: set a new one for this mailbox in the IONOS Email section.'],
  },
  {
    id: 'hostinger',
    label: 'Hostinger',
    tile: 'Hostinger Email',
    guideTitle: 'Your email password',
    guideIntro: 'For a mailbox at Hostinger, the normal email password works.',
    domains: [],
    mxPatterns: ['hostinger.com'],
    imap: ssl('imap.hostinger.com', 993),
    smtp: ssl('smtp.hostinger.com', 465),
    passwordKind: 'emailPassword',
    adminNeeded: false,
    steps: [
      {
        ...webHostPasswordStep('Hostinger'),
        linkLabel: 'Open Hostinger webmail',
        linkUrl: 'https://mail.hostinger.com',
      },
      webHostServersStep('Hostinger'),
      typePasswordStep,
    ],
    help: ['You forgot the password: set a new one for this mailbox in hPanel, under Emails.'],
  },
  {
    id: 'proton',
    label: 'Proton Mail',
    tile: 'Not supported',
    guideTitle: 'Proton Mail does not work with Music24',
    guideIntro:
      'Proton lets other apps in only through the Bridge app on a computer that is always on. Music24 cannot use it.',
    domains: ['proton.me', 'protonmail.com', 'protonmail.ch', 'pm.me'],
    mxPatterns: ['protonmail.ch'],
    imap: null,
    smtp: null,
    passwordKind: 'unsupported',
    adminNeeded: false,
    steps: [
      {
        key: 'why',
        title: 'Proton keeps other apps out',
        body: 'Your mail is safe at Proton, but Music24 cannot sign in to send your pitches.',
      },
      {
        key: 'other',
        title: 'Use another mailbox',
        body: 'A Gmail or iCloud address works in a few minutes.',
        linkLabel: 'Use another mailbox',
      },
      {
        key: 'type',
        title: 'Type the other address',
        body: 'Go back to the first step and type the address curators should answer.',
      },
    ],
    help: [],
  },
  {
    id: 'other',
    label: 'Something else',
    tile: 'GoDaddy, IONOS, Hostinger, your web host',
    guideTitle: 'Your email password',
    guideIntro:
      'For a mailbox at your web host, the normal email password works. It is the password you use to read this email in the browser of your host.',
    domains: [],
    mxPatterns: [],
    imap: null,
    smtp: null,
    passwordKind: 'emailPassword',
    adminNeeded: false,
    steps: [
      {
        key: 'password',
        title: 'Find your email password',
        body: 'It is the password you use to read this email in the browser of your host.',
      },
      {
        key: 'servers',
        title: 'Find your servers',
        body: 'Your host lists them on a help page called "email settings", "IMAP settings" or "mail client setup". You need two names that look like imap.yourhost.com and smtp.yourhost.com.',
        linkLabel: 'Where do I find my servers?',
      },
      {
        key: 'type',
        title: 'Type the servers and the password',
        body: 'Not sure about the port? Leave it: we try 993 and 465, then 587.',
      },
    ],
    help: [],
  },
];

export function mailboxProviderOf(id: MailboxProviderId): MailboxProvider {
  const provider = MAILBOX_PROVIDERS.find((entry) => entry.id === id);
  if (provider === undefined) {
    throw new Error(`Unknown mailbox provider ${id}`);
  }
  return provider;
}

/** The lowercased domain of an address, or null when it has no `@`. */
export function emailDomainOf(email: string): string | null {
  const at = email.trim().lastIndexOf('@');
  if (at < 0 || at === email.trim().length - 1) {
    return null;
  }
  return email
    .trim()
    .slice(at + 1)
    .toLowerCase()
    .replace(/\.$/, '');
}

function domainMatches(domain: string, pattern: string): boolean {
  if (pattern.endsWith('.*')) {
    // A top-level domain only (hotmail.com, hotmail.co.uk), not live.nation.com.
    const rest = domain.startsWith(pattern.slice(0, -1)) ? domain.slice(pattern.length - 1) : null;
    return rest !== null && /^[a-z]{2,3}(\.[a-z]{2})?$/.test(rest);
  }
  return domain === pattern;
}

/** The provider of a known mail domain (gmail.com, hotmail.co.uk, proton.me, ...), or null. */
export function mailboxProviderIdOfDomain(domain: string): MailboxProviderId | null {
  const lower = domain.toLowerCase();
  return MAILBOX_PROVIDERS.find((provider) => provider.domains.some((p) => domainMatches(lower, p)))?.id ?? null;
}

function mxMatches(exchange: string, pattern: string): boolean {
  return exchange === pattern || exchange.endsWith(`.${pattern}`);
}

/**
 * The provider of a domain from its MX hosts, most preferred first. The first MX host that a
 * provider knows decides. Null when no provider knows any of them.
 */
export function mailboxProviderIdOfMx(exchanges: string[]): MailboxProviderId | null {
  for (const raw of exchanges) {
    const exchange = raw.toLowerCase().replace(/\.$/, '');
    // The first provider wins: Outlook (olc.protection.outlook.com) comes before Microsoft 365.
    const provider = MAILBOX_PROVIDERS.find((entry) => entry.mxPatterns.some((p) => mxMatches(exchange, p)));
    if (provider !== undefined) {
      return provider.id;
    }
  }
  return null;
}

/**
 * The servers of a provider for one address. Zoho has regional servers: an address at `zoho.eu`
 * or a domain with a `zoho.eu` MX uses `imap.zoho.eu` / `smtp.zoho.eu`.
 */
export function mailboxHostsOf(
  id: MailboxProviderId,
  domain: string | null,
  exchanges: string[] = []
): { imap: HostSpec | null; smtp: HostSpec | null } {
  const provider = mailboxProviderOf(id);
  if (id === 'zoho') {
    const isEu =
      (domain !== null && /(^|\.)zoho(mail)?\.eu$/.test(domain)) ||
      exchanges.some((exchange) => /(^|\.)zoho\.eu\.?$/i.test(exchange));
    if (isEu && provider.imap !== null && provider.smtp !== null) {
      return {
        imap: { ...provider.imap, host: 'imap.zoho.eu' },
        smtp: { ...provider.smtp, host: 'smtp.zoho.eu' },
      };
    }
  }
  return { imap: provider.imap, smtp: provider.smtp };
}

export type MailboxAdminMessageVariant = 'googleWorkspace' | 'generic';

/**
 * The message for the mail admin of a work mailbox (frame o5). Google Workspace needs 2-Step
 * Verification and IMAP allowed. Every other mailbox gets a short generic text. Outlook and
 * Microsoft 365 are not supported, so they have no message of their own.
 */
export function mailboxAdminMessage(
  email: string,
  id: MailboxProviderId
): { variant: MailboxAdminMessageVariant; subject: string; text: string } {
  const address = email.trim();
  const domain = emailDomainOf(address);
  const isWorkspace = id === 'gmail' && domain !== null && mailboxProviderIdOfDomain(domain) !== 'gmail';
  if (isWorkspace) {
    return {
      variant: 'googleWorkspace',
      subject: 'Please allow an app password for my mailbox',
      text: `Hi,

could you allow 2-Step Verification and IMAP for my mailbox ${address} in the Google Admin console (Security → Authentication → 2-Step Verification, and Apps → Google Workspace → Gmail → End User Access → IMAP access)? I need them to make an app password, so I can send my music pitches from this mailbox with Music24. It sends only from this one mailbox.

Thank you!`,
    };
  }
  return {
    variant: 'generic',
    subject: 'Please allow IMAP and SMTP for my mailbox',
    text: `Hi,

could you allow IMAP and SMTP sign-in for my mailbox ${address}? I want to send my music pitches from it with Music24. It sends only from this one mailbox.

Thank you!`,
  };
}
